import {
  Alert,
  Flex,
  Spin,
  Typography,
} from '@duxton-web/ui';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DocumentTitle from 'react-document-title';

import {
  GET_JOURNEY_CAMPAIGN_GROUP,
  UPDATE_JOURNEY_CAMPAIGN_GROUP,
  getJourneyCampaignGroup,
  resetJourneyCampaignGroup,
  updateJourneyCampaignGroup,
} from 'omega-rtc/actions/ucd';
import { snackBar } from 'omega-rtc/components/custom/SnackBar';
import ShowIfAuthorized from 'omega-rtc/components/form/ucd/common/ShowIfAuthorized';
import {
  UCD_ACTION_TYPES,
  UCD_RESOURCE_NAMES,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import type {
  JourneyCampaignGroupCampaign,
  JourneyCampaignGroupResponse,
  UpdateJourneyCampaignGroupRequest,
} from 'omega-rtc/types/ts/journey-campaign-group';
import { isRequestsPending } from 'omega-rtc/utils/request-state-utils';
import { getRequestStateKey } from 'omega-rtc/utils/ucd-utils';
import {
  JourneyCampaignForm,
  JourneyCampaignFormValues,
  JourneyCampaignCountryOption,
} from 'omega-rtc/views/ucd/UCDCreateJourneyCampaign';

type Props = {
  params: { campaignGroupID?: string };
  router: { push: (path: string) => void };
};

const getAudienceTypeValue = (audienceType: string) => {
  const normalizedAudienceType = audienceType.trim().toUpperCase();
  if (normalizedAudienceType === 'DAX' || normalizedAudienceType === 'DRIVER') {
    return 'driver';
  }
  if (normalizedAudienceType === 'MEX' || normalizedAudienceType === 'FOODMAX') {
    return 'foodmax';
  }
  return 'passenger';
};

const getJourneyTypeValue = (journeyType: string) => (
  journeyType.trim().toUpperCase().includes('MULTI') ? 'multi-touch' : 'single-touch'
);

const UPDATE_AUDIENCE_REQUEST_VALUES: Record<string, 'PAX' | 'DAX' | 'MEX'> = {
  driver: 'DAX',
  foodmax: 'MEX',
  passenger: 'PAX',
};

const getCampaignCountryID = (campaign: JourneyCampaignGroupCampaign) => (
  Number(campaign.countryID)
);

export const getJourneyCampaignGroupEditInitialValues = (
  campaignGroup: JourneyCampaignGroupResponse,
): JourneyCampaignFormValues => ({
  audienceType: getAudienceTypeValue(campaignGroup.audienceType),
  campaignName: campaignGroup.name,
  countries: (campaignGroup.campaigns || []).map((campaign) => ({
    campaignID: campaign.campaignID,
    countryID: getCampaignCountryID(campaign),
    projectID: campaign.projectID,
    source: 'google-sheet',
    sourceUrl: campaign.journeyMetadata
      && campaign.journeyMetadata.source
      && campaign.journeyMetadata.source.url,
  })),
  endDate: dayjs(campaignGroup.endDate),
  journeyType: getJourneyTypeValue(campaignGroup.journeyType),
  productOffering: campaignGroup.productOffering,
  startDate: dayjs(campaignGroup.startDate),
  vertical: campaignGroup.vertical,
});

export const getJourneyCampaignGroupCountryOptions = (
  campaignGroup: JourneyCampaignGroupResponse,
  countries: JourneyCampaignCountryOption[],
) => Array.from(new Map([
  ...(campaignGroup.campaigns || []).map((campaign) => ({
    code: campaign.countryCode,
    countryID: getCampaignCountryID(campaign),
    name: campaign.countryName || campaign.countryCode || String(campaign.countryID),
  })),
  ...countries,
].map((country) => [country.countryID, country])).values());

export const getJourneyCampaignGroupCountryStatuses = (
  campaignGroup: JourneyCampaignGroupResponse,
) => {
  const statuses: Record<number, string> = {};
  (campaignGroup.campaigns || []).forEach((campaign) => {
    statuses[getCampaignCountryID(campaign)] = campaign.status;
  });
  return statuses;
};

export const buildUpdateJourneyCampaignGroupRequest = (
  campaignGroupID: string,
  user: string,
  values: JourneyCampaignFormValues,
): UpdateJourneyCampaignGroupRequest => ({
  audienceType: UPDATE_AUDIENCE_REQUEST_VALUES[values.audienceType],
  campaignGroupID,
  campaigns: values.countries.map(({
    campaignID,
    countryID,
    projectID,
    sourceUrl = '',
  }) => ({
    ...(campaignID ? { campaignID } : {}),
    countryID,
    ...(projectID ? { projectID } : {}),
    source: {
      type: 'GOOGLE_SHEET',
      url: sourceUrl,
    },
  })),
  endDate: values.endDate.format('YYYY-MM-DD'),
  groupType: 'JOURNEY',
  journeyType: values.journeyType === 'multi-touch' ? 'MULTI_TOUCH' : 'SINGLE_TOUCH',
  name: values.campaignName,
  productOffering: values.productOffering,
  startDate: values.startDate.format('YYYY-MM-DD'),
  user,
  vertical: values.vertical,
});

export const EditJourneyCampaignGroupForm = ({
  campaignGroup,
  countries,
  isSubmitting = false,
  onSubmit,
}: {
  campaignGroup: JourneyCampaignGroupResponse;
  countries: JourneyCampaignCountryOption[];
  isSubmitting?: boolean;
  onSubmit: (values: JourneyCampaignFormValues) => void;
}) => (
  <DocumentTitle title="Edit Journey Campaign Group - Holos">
    <div className="ucd-basic">
      <Flex align="center" gap="12px" style={{ marginBottom: '24px' }}>
        <Typography.Title level={2}>Edit Journey Campaign Group</Typography.Title>
      </Flex>
      <JourneyCampaignForm
        countries={getJourneyCampaignGroupCountryOptions(campaignGroup, countries)}
        countryStatuses={getJourneyCampaignGroupCountryStatuses(campaignGroup)}
        existingCountryIDs={(campaignGroup.campaigns || []).map(getCampaignCountryID)}
        initialValues={getJourneyCampaignGroupEditInitialValues(campaignGroup)}
        isSubmitting={isSubmitting}
        mode="edit"
        onSubmit={onSubmit}
      />
    </div>
  </DocumentTitle>
);

const EditJourneyCampaignGroup = ({ params, router }: Props) => {
  const dispatch = useDispatch();
  const campaignGroupID = params.campaignGroupID || '';
  const campaignGroup = useSelector(
    (state: any) => state.ucd.journeyCampaignGroupView,
  );
  const countries = useSelector((state: any) => state.countries.entities || []);
  const userEmail = useSelector((state: any) => _.get(state, 'app.user.email', ''));
  const getRequest = useSelector((state: any) => _.get(
    state.requests,
    getRequestStateKey(GET_JOURNEY_CAMPAIGN_GROUP),
    {},
  ));
  const updateRequest = useSelector((state: any) => _.get(
    state.requests,
    getRequestStateKey(UPDATE_JOURNEY_CAMPAIGN_GROUP),
    {},
  ));
  const previousUpdateRequest = useRef(updateRequest);

  useEffect(() => {
    if (campaignGroupID) {
      dispatch(getJourneyCampaignGroup(campaignGroupID));
    }
    return () => { dispatch(resetJourneyCampaignGroup()); };
  }, [campaignGroupID, dispatch]);

  useEffect(() => {
    const wasPending = previousUpdateRequest.current.isPending;
    previousUpdateRequest.current = updateRequest;
    if (!wasPending) return;

    if (updateRequest.isSuccessful) {
      snackBar.success({ content: 'Journey Campaign Group updated successfully' });
      router.push(UCD_ROUTES_LINK.JOURNEY_CAMPAIGN_GROUP_VIEW(campaignGroupID));
    } else if (updateRequest.isFailure) {
      snackBar.error({
        content: _.get(
          updateRequest,
          'error.response.data.message',
          'Unable to update Journey Campaign Group',
        ),
      });
    }
  }, [campaignGroupID, router, updateRequest]);

  if (getRequest.isFailure) {
    return <Alert message="Journey Campaign Group could not be loaded" showIcon type="error" />;
  }
  if (isRequestsPending(getRequest)
    || !campaignGroup
    || campaignGroup.ID !== campaignGroupID) {
    return <Flex align="center" justify="center" style={{ minHeight: 320 }}><Spin /></Flex>;
  }

  return (
    <EditJourneyCampaignGroupForm
      campaignGroup={campaignGroup}
      countries={countries}
      isSubmitting={updateRequest.isPending}
      onSubmit={(values) => {
        if (!userEmail) {
          snackBar.error({ content: 'Unable to identify the current user. Please sign in again.' });
          return;
        }

        const request = buildUpdateJourneyCampaignGroupRequest(
          campaignGroupID,
          userEmail,
          values,
        );
        const { campaignGroupID: _, ...data } = request;
        dispatch(updateJourneyCampaignGroup(campaignGroupID, data));
      }}
    />
  );
};

const AuthorizedEditJourneyCampaignGroup = (props: Props) => (
  <ShowIfAuthorized
    conditions={[{ resource: UCD_RESOURCE_NAMES.campaign, action: UCD_ACTION_TYPES.update }]}
    unauthorizedContent={(
      <Alert message="You don't have access to edit Holos campaign" showIcon type="error" />
    )}
  >
    <EditJourneyCampaignGroup {...props} />
  </ShowIfAuthorized>
);

export default AuthorizedEditJourneyCampaignGroup;
